import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthFacade } from '@iot-platform/auth';
import { ENVIRONMENT } from '@iot-platform/core';
import { SortUtil } from '@iot-platform/iot-platform-utils';
import { Affiliate, BusinessProfile, Connector, Country, Environment, Operator } from '@iot-platform/models/common';
import momentTimezones from 'moment-timezone';
import { catchError, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DirectoryService {
  readonly environment: Environment = inject(ENVIRONMENT);
  readonly http: HttpClient = inject(HttpClient);
  readonly authFacade: AuthFacade = inject(AuthFacade);

  getAffiliates(): Observable<Affiliate[]> {
    const businessProfile: BusinessProfile = this.authFacade.selectedBusinessProfile();
    return this.http.get<Affiliate[]>(`${this.environment.api.url}${this.environment.api.endpoints.affiliates}?entityId=${businessProfile.entityId}`).pipe(
      map((data: Affiliate[]) =>
        data.sort(SortUtil.sortBy('id', true)).map((affiliate: Affiliate) => ({
          ...affiliate,
          label: `${affiliate.id} : ${affiliate.name}`
        }))
      ),
      catchError(() => of([]))
    );
  }

  getOperators(): Observable<Operator[]> {
    return this.http.get<string[]>(`${this.environment.api.url}${this.environment.api.endpoints.operators}`).pipe(
      map((data: string[]) => data.sort().map((name: string) => ({ id: name, name }))),
      catchError(() => of([]))
    );
  }

  getTimezones(): Observable<{ key: string; value: string }[]> {
    return of(
      momentTimezones.tz.names().map((timezone: string) => ({
        key: `${timezone} ${momentTimezones.tz(timezone).format('Z')}`,
        value: timezone
      }))
    );
  }

  getDefaultImage(): string {
    return 'assets/images/device/Device_Default.png';
  }

  getConnectors(): Observable<Connector[]> {
    return this.http.get<Connector[]>(`${this.environment.api.url}${this.environment.api.endpoints.connectors}`).pipe(catchError(() => of([])));
  }

  getCountries(): Observable<Country[]> {
    return this.http.get<Country[]>('/assets/data/countries.json');
  }

  getImages(): Observable<string[]> {
    return this.http.get<string[]>('/assets/data/devices-gallery.json');
  }
}
